// COMP-BREAK-GLASS — دیالوگ دسترسی اضطراری (AP-IAM-04؛ US-IAM-041، AC-IAM-041.1، BR-IAM-042، 043): هشدار صریح، کد دلیل از فهرست و
// توضیح آزاد اجباری، تأیید مجدد هویت با ورود دوباره Keycloak (Popup prompt=login — C-AGENT-398) و صدور دوباره توکن مستأجر با auth_time
// تازه، سپس POST access-overrides. جزء مشترک: پرونده مراجعه‌کننده و در آینده صفحات بالینی CLN.
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap, type AccessOverrideView } from '@phoenix/api-client';
import { useAuth, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { applyServerErrors, FormDialog, SelectRHF, TextFieldRHF, zRequiredText } from '@phoenix/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export const COMP_BREAK_GLASS = 'COMP-BREAK-GLASS';

export const BREAK_GLASS_REASONS = [
  'emergency_care',
  'covering_colleague',
  'continuity_of_care',
  'patient_request',
  'other',
] as const;

const schema = z.object({
  reasonCode: z.enum(BREAK_GLASS_REASONS, { error: 'common:validation.required' }),
  reason: zRequiredText(500),
});

type BreakGlassForm = z.input<typeof schema>;

export interface BreakGlassDialogProps {
  open: boolean;
  patientId: string;
  onClose: () => void;
  onActivated: (view: AccessOverrideView) => void;
}

export function BreakGlassDialog({ open, patientId, onClose, onActivated }: BreakGlassDialogProps) {
  const { t } = useTranslation('iam');
  const auth = useAuth();
  const { tenantApi, resume } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>(undefined);
  const [reauthFailed, setReauthFailed] = useState(false);
  const { control, handleSubmit, formState, setError, reset } = useForm<BreakGlassForm>({
    resolver: zodResolver(schema),
    defaultValues: { reasonCode: 'emergency_care', reason: '' },
  });

  const activate = useMutation({
    mutationFn: async (form: BreakGlassForm) => {
      // AC-IAM-041.1: تأیید هویت با ورود دوباره (رمز و در صورت فعال بودن ۲FA) و توکن مستأجر تازه با auth_time.
      if (!(await auth.reauthenticate()) || !(await resume())) {
        throw new Error('reauthentication_failed');
      }
      return unwrap(
        tenantApi.POST('/api/v1/identity/access-overrides', {
          body: { patientId, reasonCode: form.reasonCode, reason: form.reason.trim() },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      );
    },
    onMutate: () => {
      setSubmitError(undefined);
      setReauthFailed(false);
    },
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (view) => {
      reset();
      onActivated(view);
    },
    onError: (error) => {
      if (!isApiError(error)) {
        setReauthFailed(true);
        return;
      }
      const applied = applyServerErrors(error, setError, { fields: ['reasonCode', 'reason'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open={open}
      titleKey="iam:break_glass.title"
      screenId={COMP_BREAK_GLASS}
      onSubmit={() => void handleSubmit((form) => activate.mutate(form))()}
      onClose={() => {
        reset();
        setSubmitError(undefined);
        setReauthFailed(false);
        onClose();
      }}
      isDirty={formState.isDirty}
      isSubmitting={activate.isPending}
      submitError={submitError}
      submitKey="iam:break_glass.confirm"
    >
      <Alert severity="error" data-testid="break-glass-warning">
        {t('iam:break_glass.warning')}
      </Alert>
      <SelectRHF
        control={control}
        name="reasonCode"
        labelKey="iam:break_glass.reason_code"
        required
        fullWidth
        options={BREAK_GLASS_REASONS.map((value) => ({ value, labelKey: `iam:break_glass.reasons.${value}` }))}
      />
      <TextFieldRHF
        control={control}
        name="reason"
        labelKey="iam:break_glass.reason"
        helperKey="iam:break_glass.reason_hint"
        required
        fullWidth
        multiline
        minRows={3}
        autoFocus
        slotProps={{ htmlInput: { 'data-testid': 'break-glass-reason', maxLength: 500 } }}
      />
      <Typography variant="body2" color="text.secondary">
        {t('iam:break_glass.reauth_hint')}
      </Typography>
      {reauthFailed && (
        <Alert severity="error" data-testid="break-glass-reauth-failed">
          {t('iam:break_glass.reauth_failed')}
        </Alert>
      )}
    </FormDialog>
  );
}
